<script setup lang="ts">

import {useCurrentWorkspaceUser} from "~/stores/spaces";
import type {Currency, PaymentMethod} from "~/types/subscription.types";

const {isOwner, workspaceOwnerEmail} = useCurrentWorkspaceUser()
const {plan, active, currency: accountCurrency} = useSubscriptionForEnvironment()

const connections = ref(plan.value?.connections || 5)
const currency = ref<Currency>('USD')
const paymentMethod = ref<PaymentMethod>("CREDIT_CARD_USD")

const PLAN_TYPE = 'AXONIQ_CONSOLE_PRO'

watch([accountCurrency], () => {
  currency.value = accountCurrency.value ?? 'USD'
}, {immediate: true})

watch([plan], () => {
  if (plan.value) {
    connections.value = plan.value.connections
  }
})

watch([currency], () => {
  if (paymentMethod.value === "INVOICE") {
    return
  }
  if (currency.value === 'USD') {
    paymentMethod.value = 'CREDIT_CARD_USD'
  } else {
    paymentMethod.value = 'CREDIT_CARD_EUR'
  }
})

const hasFulfilledPlan = computed(() => {
  return plan.value && plan.value?.status !== 'PENDING_ACTIVATION'
})
</script>
<template>
  <v-alert v-if="!isOwner" type="info" class="my-4">
    Only the owner of the workspace, {{ workspaceOwnerEmail }} can manage the subscription.
  </v-alert>
  <template v-if="hasFulfilledPlan">
    <SubscriptionFormProUpdate/>
  </template>
  <template v-else-if="active">
    <SubscriptionFormProAddition/>
  </template>
  <template v-else>
    <!-- No update, no addition, there's no plan yet -->
    <p>
      You have chosen the AxonIQ Console Professional plan. It contains everything your need to get started seriously
      in Production.
    </p>

    <!-- Currency can only be changed if they did not have a subscription yet -->
    <SubscriptionFormCurrencyChooser v-model="currency"/>

    <div class="my-4">
      <p class="h5">
        How many connections do you need?
      </p>
      <SubscriptionFormProConnectionChooser v-model="connections" :currency="currency" :plan-type="PLAN_TYPE"
                                            class="mt-2"/>
    </div>

    <SubscriptionFormPaymentMethodChooser :currency="currency" v-model="paymentMethod"/>

    <SubscriptionFormProNewInvoiceBased v-if="paymentMethod === 'INVOICE'" :plan-type="PLAN_TYPE"
                                        :amount="connections" :currency="currency" @close="$router.push({query: {}})"/>
    <SubscriptionFormProNewDirectBased v-else :plan-type="PLAN_TYPE" :connections="connections" :currency="currency"
                                       :payment-method="paymentMethod" @close="$router.push({query: {}})"/>
  </template>
</template>
